// composables/useTranslations.ts
import { computed } from 'vue';
import { de } from '../i18n/de';
import { en } from '../i18n/en';
import { es } from '../i18n/es';
import { DIFFICULTY_KEYS, LANGUAGE_KEYS, TAG_KEYS } from '../utils/constants';
import { currentLanguage } from './useLanguage';

const translationModules = {
  en,
  de: de satisfies TranslationStructure,
  es: es satisfies TranslationStructure,
} as const;

// For hover hints (only show most important languages)
type TranslationUnion =
  | typeof en // Always show English
  | typeof de // Show German
  | typeof es; // Show Spanish

// For structure checking (checks ALL languages)
type TranslationStructure = {
  [K in keyof typeof en]: string;
};

const translations = Object.fromEntries(
  Object.entries(translationModules).map(([code, module]) => [
    code,
    {
      translations: module,
      nativeName: module.nativeName,
    },
  ]),
) as Record<Languages, { translations: TranslationUnion; nativeName: string }>;

export const LANGUAGE_CODES = Object.keys(translationModules) as Languages[];

export type Languages = keyof typeof translationModules;
export type Translation = TranslationUnion;
export type DifficultyLevel = (typeof DIFFICULTY_KEYS)[number];
export type Language = (typeof LANGUAGE_KEYS)[number];
export type TagKey = (typeof TAG_KEYS)[number];

export const languagesDropDownObject = Object.entries(translationModules).map(
  ([, module]) => ({
    code: module.languageCode,
    label: module.nativeName,
  }),
);

export function useTranslations() {
  // Returns the entire translation object for current language
  const t = computed(() => translations[currentLanguage.value].translations);
  return t;
}
